import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { PetSetupHandoffs } from './petSetupHandoff.js';
import { createBuilderDraftSession } from './companionParts.js';
const useClientLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;
const noSubscribe = () => () => { };
const noSnapshot = () => 0;
export function usePetSetupHandoff(owner, client, serviceKey, enabled, conversation, factory = createBuilderDraftSession) {
    const session = useMemo(() => ({ serviceKey, enabled, handoffs: new PetSetupHandoffs(owner, client) }), [owner, client, serviceKey, enabled]);
    const [attempt, setAttempt] = useState(0), activeLoad = useRef(undefined);
    const [loaded, setLoaded] = useState();
    const [failure, setFailure] = useState();
    const drafts = enabled && loaded?.session === session && loaded.factory === factory && loaded.token === activeLoad.current ? loaded.drafts : undefined;
    useSyncExternalStore(drafts?.subscribe ?? noSubscribe, drafts?.snapshot ?? noSnapshot, drafts?.snapshot ?? noSnapshot);
    const draftsLive = useRef(drafts);
    draftsLive.current = drafts;
    useClientLayoutEffect(() => {
        if (!enabled)
            return;
        const token = {};
        activeLoad.current = token;
        let current = true, owned;
        void Promise.resolve().then(() => current ? factory(owner, client) : undefined).then(value => {
            owned = value;
            if (!current) {
                value?.clear();
                return;
            }
            if (!value || value.owner !== owner || value.transport !== client)
                throw new Error('Setup session unavailable');
            setLoaded({ session, factory, token, drafts: value });
            setFailure(undefined);
        }).catch(() => {
            if (!current)
                return;
            owned?.clear();
            setLoaded(undefined);
            setFailure({ session, factory });
        });
        return () => { current = false; owned?.clear(); if (activeLoad.current === token)
            activeLoad.current = undefined; };
    }, [session, owner, client, enabled, factory, attempt]);
    const live = useRef({ session, enabled, conversation });
    live.current = { session, enabled, conversation };
    const committed = useRef(undefined);
    const request = useRef(undefined);
    const [notice, setNotice] = useState();
    const [checking, setChecking] = useState();
    useClientLayoutEffect(() => {
        const previous = committed.current;
        if (previous && previous.session !== session) {
            previous.session.handoffs.clear();
            request.current?.abort();
            request.current = undefined;
            setChecking(undefined);
            setNotice(undefined);
        }
        const current = { session };
        committed.current = current;
        if (!enabled) {
            session.handoffs.clear();
            request.current?.abort();
            request.current = undefined;
            setChecking(undefined);
            setNotice(undefined);
        }
        return () => queueMicrotask(() => {
            if (committed.current !== current)
                return;
            committed.current = undefined;
            session.handoffs.clear();
            request.current?.abort();
        });
    }, [session, enabled]);
    const routeForDraft = () => {
        const draft = drafts?.inspect();
        return draft?.identity.kind === 'edit' ? { view: 'pet', petId: draft.identity.petId, tab: 'setup' }
            : { view: 'create', ...(draft?.identity.kind === 'create' && draft.identity.templateId ? { templateId: draft.identity.templateId } : {}) };
    };
    const ready = () => !!drafts && draftsLive.current === drafts && live.current.enabled && live.current.session === session && loaded?.token === activeLoad.current;
    const failed = enabled && failure?.session === session && failure.factory === factory;
    return { ...session, drafts, ready: !!drafts, failed, checking, notice,
        retry: () => { if (failed && !drafts && live.current.session === session) {
            setFailure(undefined);
            setAttempt(value => value + 1);
        } },
        label: () => drafts?.inspect() ? 'Resume existing setup' : 'Review PET setup',
        resume: (open) => { if (ready()) {
            drafts.requestResume();
            open(routeForDraft());
        } },
        open: async (token, conversationId, open) => {
            if (request.current || !ready())
                return;
            const controller = new AbortController();
            request.current = controller;
            setChecking(token);
            setNotice(undefined);
            const current = () => ready() && live.current.conversation() === conversationId;
            let timer;
            try {
                const description = await Promise.race([session.handoffs.review(token, conversationId, signal => client.petRequest('/v1/pets/catalog', { signal }), current, controller.signal), new Promise((_resolve, reject) => {
                        timer = setTimeout(() => { controller.abort(); reject(new Error('Setup availability check timed out.')); }, 15000);
                    })]);
                if (!current())
                    return;
                if (drafts.inspect()) {
                    drafts.requestResume();
                    open(routeForDraft());
                    return;
                }
                if (!drafts.claim({ kind: 'create' }, description))
                    throw new Error('An existing setup must be reviewed first.');
                session.handoffs.consume(token);
                drafts.requestResume();
                open({ view: 'create' });
            }
            catch (error) {
                if (current())
                    setNotice({ token, text: controller.signal.aborted ? 'The availability check timed out. Try again to check current access.'
                            : error instanceof Error ? error.message : 'The setup could not be opened. Your existing draft has been kept.' });
            }
            finally {
                clearTimeout(timer);
                if (request.current === controller)
                    request.current = undefined;
                if (live.current.session === session)
                    setChecking(undefined);
            }
        } };
}
//# sourceMappingURL=usePetSetupHandoff.js.map